import { Link, useNavigate } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Banknote,
  Bell,
  Briefcase,
  CalendarDays,
  Cake,
  CheckSquare,
  Inbox,
  Receipt,
  Sparkles,
  TrendingUp,
  User as UserIcon,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

import { BeatStatusCard } from "@/components/beat-status-card";
import { Avatar as PersonAvatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { BarChart } from "@/components/ui/bar-chart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErrorMessage } from "@/components/ui/error-message";
import { KpiCard } from "@/components/ui/kpi-card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type Paginated } from "@/lib/api";
import { formatDate, formatPHPCompact, formatRelative } from "@/lib/format";
import { useMyEmployee } from "@/lib/hooks";
import { ModuleCode, useIsModuleEnabled } from "@/lib/me";
import { cn } from "@/lib/utils";
import type {
  ApprovalTask,
  Employee,
  JobOpening,
  Notification,
  PayrollEntry,
  Severity,
  User,
} from "@/lib/types";

const SEVERITY_VARIANT: Record<
  Severity,
  "default" | "secondary" | "warning" | "destructive"
> = {
  info: "secondary",
  success: "default",
  warning: "warning",
  error: "destructive",
};

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function DashboardPage() {
  // Payroll and recruitment API endpoints are module-gated (403
  // ``module_disabled`` when off). Gate the queries and their surfaces on the
  // tenant's enabled modules so we never fire a doomed request or leak a raw
  // error onto the dashboard — mirrors the sidebar, which hides disabled-
  // module nav entries rather than surfacing an error.
  const payrollEnabled = useIsModuleEnabled(ModuleCode.PAYROLL);
  const recruitmentEnabled = useIsModuleEnabled(ModuleCode.RECRUITMENT);

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<User>("/accounts/users/me/"),
  });

  // KPI data: count-only queries are page_size=1 to keep payload small.
  const employees = useQuery({
    queryKey: ["employees", "count"],
    queryFn: () =>
      api.get<Paginated<Employee>>("/employees/employees/?page_size=1"),
  });

  const recentRuns = useQuery({
    queryKey: ["payroll-entries", "dashboard"],
    queryFn: () =>
      api.get<Paginated<PayrollEntry>>(
        "/payroll/payroll-entries/?ordering=-created_at&page_size=12&state=submitted",
      ),
    enabled: payrollEnabled,
  });

  const openings = useQuery({
    queryKey: ["openings", "open"],
    queryFn: () =>
      api.get<Paginated<JobOpening>>(
        "/recruitment/job-openings/?page_size=1&state=open",
      ),
    enabled: recruitmentEnabled,
  });

  const pendingApprovalsCount = useQuery({
    queryKey: ["approval-tasks", "pending-count"],
    queryFn: () =>
      api.get<Paginated<ApprovalTask>>(
        "/workflows/tasks/?state=active&page_size=1",
      ),
  });

  const recentNotifications = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () =>
      api.get<Notification[] | Paginated<Notification>>(
        "/notifications/inbox/?limit=6",
      ),
  });

  const recent: Notification[] = Array.isArray(recentNotifications.data)
    ? recentNotifications.data
    : (recentNotifications.data?.results ?? []);

  const firstName = me.data?.first_name;

  // Aggregate the latest approved (submitted) run for the "Payroll this month" KPI.
  const latestRun = recentRuns.data?.results[0];
  const latestRunGross = latestRun
    ? Number.parseFloat(latestRun.total_gross || "0")
    : null;

  // On-time payroll: share of recent approved runs whose bank payment has
  // been recorded (Mark paid). Cheap heuristic for a dashboard headline.
  const allRecent = recentRuns.data?.results.length ?? 0;
  const onTimeShare =
    allRecent > 0
      ? Math.round(
          (recentRuns.data!.results.filter(
            (r) => Boolean(r.bank_je_id),
          ).length /
            allRecent) *
            100,
        )
      : null;

  // Workforce panel is always shown; payroll/hiring join it only when their
  // module is enabled. Drives the 3-up grid's column count below.
  const overviewPanelCount =
    1 + (payrollEnabled ? 1 : 0) + (recruitmentEnabled ? 1 : 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Hero — time-of-day greeting + sub-line. */}
      <section className="flex flex-col gap-1.5">
        <h1 className="text-3xl font-bold tracking-tight text-navy">
          {timeOfDayGreeting()}
          {firstName ? `, ${firstName}` : ""} 👋
        </h1>
        <p className="text-[15px] text-slate">
          Here's your workforce overview for today.
        </p>
      </section>

      {/* 5-up KPI grid. */}
      <section
        aria-label="Key metrics"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        <KpiCard
          label="Total employees"
          value={
            employees.data
              ? employees.data.count.toLocaleString("en-PH")
              : "—"
          }
          loading={employees.isLoading}
          error={employees.isError}
          icon={Users}
          iconTone="blue"
        />
        {payrollEnabled && (
          <>
            <KpiCard
              label="Latest payroll run"
              value={
                latestRunGross !== null ? formatPHPCompact(latestRunGross) : "—"
              }
              loading={recentRuns.isLoading}
              error={recentRuns.isError}
              icon={Wallet}
              iconTone="green"
              trend={
                latestRun
                  ? {
                      direction: "flat",
                      value: latestRun.state.toUpperCase(),
                      context: latestRun.name,
                    }
                  : undefined
              }
            />
            <KpiCard
              label="On-time payroll"
              value={onTimeShare !== null ? `${onTimeShare}%` : "—"}
              loading={recentRuns.isLoading}
              error={recentRuns.isError}
              icon={TrendingUp}
              iconTone="cyan"
              trend={
                allRecent > 0
                  ? {
                      direction: "flat",
                      value: `last ${allRecent} runs`,
                    }
                  : undefined
              }
            />
          </>
        )}
        {recruitmentEnabled && (
          <KpiCard
            label="Open positions"
            value={
              openings.data ? openings.data.count.toLocaleString("en-PH") : "—"
            }
            loading={openings.isLoading}
            error={openings.isError}
            icon={Briefcase}
            iconTone="purple"
          />
        )}
        <KpiCard
          label="Pending approvals"
          value={
            pendingApprovalsCount.data
              ? pendingApprovalsCount.data.count.toLocaleString("en-PH")
              : "—"
          }
          loading={pendingApprovalsCount.isLoading}
          error={pendingApprovalsCount.isError}
          icon={CheckSquare}
          iconTone="amber"
        />
      </section>

      {/* Mockup-style 3-up: Workforce / Payroll Overview / Hiring Pipeline.
          Payroll and hiring panels drop out when their module is off; the
          grid narrows so the remaining panels stay balanced rather than
          leaving an empty column. */}
      <section
        className={cn(
          "grid gap-4",
          overviewPanelCount === 3 && "xl:grid-cols-3",
          overviewPanelCount === 2 && "xl:grid-cols-2",
        )}
      >
        <WorkforceStatusPanel total={employees.data?.count ?? null} />
        {payrollEnabled && (
          <PayrollOverviewPanel
            latestRun={latestRun}
            onTimeShare={onTimeShare}
          />
        )}
        {recruitmentEnabled && <HiringPipelinePanel />}
      </section>

      {/* Bottom 3-up: New joiners / Anniversaries / Pending approvals. */}
      <section className="grid gap-4 xl:grid-cols-3">
        <NewJoinersCard />
        <WorkAnniversariesCard />
        <PendingApprovalsCard />
      </section>

      {/* Existing analytical bits — preserved below the mockup hero. The
          payroll chart drops out when payroll is off; Quick access then
          spans the row on its own. */}
      {payrollEnabled ? (
        <section className="grid gap-4 lg:grid-cols-3">
          <RecentRunsChart />
          <QuickAccessCard />
        </section>
      ) : (
        <section>
          <QuickAccessCard />
        </section>
      )}

      <BeatStatusCard />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent activity</CardTitle>
              <CardDescription>Latest 6 notifications</CardDescription>
            </div>
            <Link
              to="/notifications"
              className="inline-flex items-center gap-1 text-sm font-medium text-blue hover:text-blue-hover"
            >
              View all
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentNotifications.isLoading && (
            <div className="space-y-3 py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {recentNotifications.isError && (
            <ErrorMessage
              error={recentNotifications.error}
              fallback="Couldn't load activity."
            />
          )}
          {recentNotifications.isSuccess && recent.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-slate">
              <Bell size={24} strokeWidth={1.5} aria-hidden="true" />
              <p className="text-sm">No activity yet.</p>
            </div>
          )}
          <ul className="divide-y divide-slate-200">
            {recent.map((n) => (
              <li
                key={n.id}
                className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {!n.read_at && (
                      <span
                        className="inline-block h-2 w-2 shrink-0 rounded-full bg-blue"
                        aria-label="Unread"
                      />
                    )}
                    <p className="truncate text-sm font-medium text-navy">
                      {n.subject}
                    </p>
                    <Badge variant={SEVERITY_VARIANT[n.severity]}>
                      {n.severity}
                    </Badge>
                  </div>
                  {n.body && (
                    <p className="truncate text-xs text-slate">{n.body}</p>
                  )}
                </div>
                <span className="whitespace-nowrap text-xs tabular-nums text-slate">
                  {formatRelative(n.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Closing CTA banner. */}
      <section
        aria-label="Analytics call to action"
        className="relative overflow-hidden rounded-card p-7 text-white shadow-card-lg
          bg-[radial-gradient(circle_at_78%_22%,rgba(6,182,212,0.55),transparent_28%),linear-gradient(135deg,#071126_0%,#0b1b3d_55%,#0f766e_100%)]"
      >
        <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
          <div className="flex items-start gap-4">
            <span
              aria-hidden="true"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur"
            >
              <Sparkles size={22} strokeWidth={1.75} />
            </span>
            <div>
              <h2 className="text-xl font-bold tracking-tight">
                People are your greatest asset.
              </h2>
              <p className="mt-1 text-sm text-white/70">
                Empower them. Support them. Celebrate them.
              </p>
            </div>
          </div>
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 rounded-btn bg-gradient-to-r from-blue to-blue-hover px-5 py-3 text-sm font-bold shadow-[0_14px_28px_rgba(37,99,235,0.35)] transition-transform hover:scale-[1.02]"
          >
            Explore HR analytics
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workforce status panel
// ---------------------------------------------------------------------------

// Each bucket folds one or more backend statuses. ``active`` is the legacy
// alias that predates the probationary/regular split and is what imported
// employees default to — count it as Regular so they don't silently vanish
// from the breakdown (donut would read all-zero against a non-zero total).
const WORKFORCE_SEGMENTS = [
  { key: "regular", label: "Regular", color: "#10B981", statuses: ["regular", "active"] }, // green
  { key: "probationary", label: "Probationary", color: "#06B6D4", statuses: ["probationary"] }, // cyan
  { key: "on_leave", label: "On leave", color: "#F59E0B", statuses: ["on_leave"] }, // amber
  { key: "awol", label: "AWOL", color: "#EF4444", statuses: ["awol"] }, // red
  { key: "suspended", label: "Suspended", color: "#94A3B8", statuses: ["suspended"] }, // slate
] as const;

function WorkforceStatusPanel({ total }: { total: number | null }) {
  // Fan out one count-only query per segment. Each returns Paginated<…>
  // with `count` set; we never materialise the result list.
  const queries = useQueries({
    queries: WORKFORCE_SEGMENTS.map((seg) => ({
      queryKey: ["employees", "status-count", seg.key],
      queryFn: () =>
        api.get<Paginated<Employee>>(
          `/employees/employees/?status__in=${seg.statuses.join(",")}&page_size=1`,
        ),
      staleTime: 60_000,
    })),
  });

  const segments = WORKFORCE_SEGMENTS.map((seg, i) => ({
    ...seg,
    count: queries[i].data?.count ?? 0,
    loading: queries[i].isLoading,
  }));
  const tracked = segments.reduce((sum, s) => sum + s.count, 0);
  const allLoading = queries.some((q) => q.isLoading);

  // Conic-gradient stops are positional %. Compute them once from segment
  // counts so the donut visually mirrors the legend.
  const donutGradient = (() => {
    if (tracked === 0) return "conic-gradient(#E2E8F0 0 100%)";
    let cursor = 0;
    const stops: string[] = [];
    for (const s of segments) {
      if (s.count === 0) continue;
      const pct = (s.count / tracked) * 100;
      stops.push(`${s.color} ${cursor}% ${cursor + pct}%`);
      cursor += pct;
    }
    return `conic-gradient(${stops.join(", ")})`;
  })();

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold tracking-tight text-navy">
            Workforce status
          </h3>
          <p className="mt-0.5 text-xs text-slate">
            Real-time breakdown by employment status
          </p>
        </div>
      </div>
      <div className="flex items-center gap-5">
        <div className="relative shrink-0">
          <div
            aria-hidden="true"
            className="h-[140px] w-[140px] rounded-full"
            style={{ background: donutGradient }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 m-auto flex h-[88px] w-[88px] flex-col items-center justify-center rounded-full bg-white text-center"
          >
            <span className="text-xl font-bold tabular-nums text-navy">
              {total === null ? "—" : total.toLocaleString("en-PH")}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate">
              Total
            </span>
          </div>
        </div>
        <ul className="min-w-0 flex-1 space-y-2">
          {segments.map((s) => {
            const pct =
              tracked > 0 ? Math.round((s.count / tracked) * 1000) / 10 : 0;
            return (
              <li
                key={s.key}
                className="flex items-center justify-between gap-3 text-[13px]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: s.color }}
                  />
                  <span className="truncate text-slate-700">{s.label}</span>
                </span>
                <span className="shrink-0 whitespace-nowrap pl-2 font-semibold tabular-nums text-navy">
                  {allLoading ? "—" : `${s.count.toLocaleString("en-PH")} (${pct.toFixed(1)}%)`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <Link
        to="/employees"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-btn border border-slate-200 bg-gradient-to-b from-white to-slate-50 py-2 text-sm font-semibold text-blue transition-colors hover:bg-blue/5"
      >
        View all employees
        <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Payroll overview panel
// ---------------------------------------------------------------------------

function PayrollOverviewPanel({
  latestRun,
  onTimeShare,
}: {
  latestRun: PayrollEntry | undefined;
  onTimeShare: number | null;
}) {
  const completion = onTimeShare ?? (latestRun ? 0 : null);
  const completionLabel =
    completion === null ? "—" : `${Math.round(completion)}%`;

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold tracking-tight text-navy">
            Payroll overview
          </h3>
          <p className="mt-0.5 text-xs text-slate">
            {latestRun ? latestRun.name : "Most recent run"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-5">
        <div className="relative shrink-0">
          <div
            aria-hidden="true"
            className="h-[140px] w-[140px] rounded-full"
            style={{
              background:
                completion === null
                  ? "conic-gradient(#E2E8F0 0 100%)"
                  : `conic-gradient(#10B981 0 ${completion}%, #E2E8F0 ${completion}% 100%)`,
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 m-auto flex h-[88px] w-[88px] flex-col items-center justify-center rounded-full bg-white text-center"
          >
            <span className="text-xl font-bold tabular-nums text-navy">
              {completionLabel}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate">
              On-time
            </span>
          </div>
        </div>
        <dl className="min-w-0 flex-1 space-y-2 text-[13px]">
          <DetailRow
            label="Latest state"
            value={
              latestRun ? (
                <span className="uppercase">{latestRun.state}</span>
              ) : (
                "—"
              )
            }
          />
          <DetailRow
            label="Total gross"
            value={
              latestRun ? formatPHPCompact(latestRun.total_gross || "0") : "—"
            }
          />
          <DetailRow
            label="Total net"
            value={
              latestRun ? formatPHPCompact(latestRun.total_net || "0") : "—"
            }
          />
          <DetailRow
            label="Posted"
            value={latestRun ? formatDate(latestRun.created_at) : "—"}
          />
        </dl>
      </div>
      <Link
        to="/payroll-runs"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-btn border border-slate-200 bg-gradient-to-b from-white to-slate-50 py-2 text-sm font-semibold text-blue transition-colors hover:bg-blue/5"
      >
        View payroll runs
        <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </Card>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 last:border-0 last:pb-0">
      <dt className="truncate text-slate">{label}</dt>
      <dd className="shrink-0 whitespace-nowrap pl-2 font-semibold tabular-nums text-navy">
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hiring pipeline panel
// ---------------------------------------------------------------------------

const PIPELINE_STAGES = [
  { status: "open", label: "Open", gradient: "from-blue to-purple", width: 100 },
  { status: "replied", label: "Replied", gradient: "from-[#4F46E5] to-purple", width: 82 },
  { status: "shortlisted", label: "Shortlisted", gradient: "from-purple to-cyan", width: 65 },
  { status: "accepted", label: "Hired", gradient: "from-cyan to-green", width: 48 },
] as const;

function HiringPipelinePanel() {
  const queries = useQueries({
    queries: PIPELINE_STAGES.map((stage) => ({
      queryKey: ["applicants", "stage-count", stage.status],
      queryFn: () =>
        api.get<Paginated<unknown>>(
          `/recruitment/applicants/?status=${stage.status}&page_size=1`,
        ),
      staleTime: 60_000,
    })),
  });

  const stages = PIPELINE_STAGES.map((stage, i) => ({
    ...stage,
    count: queries[i].data?.count ?? 0,
    loading: queries[i].isLoading,
  }));

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold tracking-tight text-navy">
            Hiring pipeline
          </h3>
          <p className="mt-0.5 text-xs text-slate">
            Applicant counts by current status
          </p>
        </div>
      </div>
      <div className="flex items-center gap-5">
        {/* CSS-only funnel. Each band is a trapezoid via clip-path. */}
        <div
          aria-hidden="true"
          className="flex shrink-0 flex-col items-center gap-1.5"
        >
          {stages.map((s) => (
            <div
              key={s.status}
              className={cn(
                "h-9 bg-gradient-to-r",
                s.gradient,
              )}
              style={{
                width: `${s.width}px`,
                clipPath: "polygon(8% 0, 92% 0, 82% 100%, 18% 100%)",
              }}
            />
          ))}
        </div>
        <ul className="min-w-0 flex-1 space-y-2 text-[13px]">
          {stages.map((s) => (
            <li
              key={s.status}
              className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 last:border-0 last:pb-0"
            >
              <span className="truncate text-slate-700">{s.label}</span>
              <span className="shrink-0 whitespace-nowrap pl-2 font-semibold tabular-nums text-navy">
                {s.loading ? "—" : s.count.toLocaleString("en-PH")}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Link
        to="/openings"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-btn border border-slate-200 bg-gradient-to-b from-white to-slate-50 py-2 text-sm font-semibold text-blue transition-colors hover:bg-blue/5"
      >
        View all openings
        <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// New joiners card
// ---------------------------------------------------------------------------

function NewJoinersCard() {
  const joiners = useQuery({
    queryKey: ["employees", "new-joiners"],
    queryFn: () =>
      api.get<Paginated<Employee>>(
        "/employees/employees/?ordering=-date_of_joining&page_size=10&is_active=true",
      ),
  });

  const rows = (joiners.data?.results ?? [])
    .filter((e) => e.date_of_joining)
    .slice(0, 4);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserPlus
                size={16}
                strokeWidth={1.75}
                className="text-blue"
                aria-hidden="true"
              />
              New joiners
            </CardTitle>
            <CardDescription>Most recent hires</CardDescription>
          </div>
          <Link
            to="/employees?ordering=-date_of_joining"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue hover:text-blue-hover"
          >
            View all
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {joiners.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {!joiners.isLoading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-6 text-slate">
            <UserPlus size={22} strokeWidth={1.5} aria-hidden="true" />
            <p className="text-sm">No new joiners yet.</p>
          </div>
        )}
        <ul className="flex flex-col gap-3">
          {rows.map((e) => (
            <li key={e.id}>
              <Link
                to={`/employees/${e.id}`}
                className="group grid grid-cols-[40px_1fr_auto] items-center gap-3 rounded-btn -mx-1.5 px-1.5 py-1.5 transition-colors hover:bg-slate-50"
              >
                <Avatar employee={e} tone="blue" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy group-hover:text-blue">
                    {e.full_name || `${e.first_name} ${e.last_name}`.trim()}
                  </p>
                  <p className="truncate text-[11px] font-mono text-slate">
                    {e.employee_id}
                  </p>
                </div>
                <span className="whitespace-nowrap text-[11px] tabular-nums text-slate">
                  {e.date_of_joining ? formatDate(e.date_of_joining) : "—"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Work anniversaries card
// ---------------------------------------------------------------------------

function WorkAnniversariesCard() {
  // Fetch a broad page of active employees and filter client-side by
  // anniversary month — there's no dedicated "anniversary this month"
  // endpoint, and the dataset is small enough that this is fine.
  const employees = useQuery({
    queryKey: ["employees", "anniversaries-pool"],
    queryFn: () =>
      api.get<Paginated<Employee>>(
        "/employees/employees/?page_size=200&is_active=true",
      ),
    staleTime: 5 * 60_000,
  });

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();

  const anniversaries = (employees.data?.results ?? [])
    .filter((e) => e.date_of_joining)
    .map((e) => {
      const joined = new Date(e.date_of_joining as string);
      const years =
        now.getFullYear()
        - joined.getFullYear()
        - (now.getMonth() < joined.getMonth()
            || (now.getMonth() === joined.getMonth()
              && now.getDate() < joined.getDate())
          ? 1
          : 0);
      return { employee: e, joinedMonth: joined.getMonth(), joinedDay: joined.getDate(), years };
    })
    .filter((row) => row.joinedMonth === currentMonth && row.years >= 1)
    .sort((a, b) => {
      // Order: today first, then upcoming this month, then past dates.
      const aDelta = a.joinedDay - currentDay;
      const bDelta = b.joinedDay - currentDay;
      const aSign = aDelta < 0 ? 1 : 0;
      const bSign = bDelta < 0 ? 1 : 0;
      if (aSign !== bSign) return aSign - bSign;
      return Math.abs(aDelta) - Math.abs(bDelta);
    })
    .slice(0, 4);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Cake
                size={16}
                strokeWidth={1.75}
                className="text-purple"
                aria-hidden="true"
              />
              Work anniversaries
            </CardTitle>
            <CardDescription>
              {MONTHS[currentMonth]} milestones
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {employees.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {!employees.isLoading && anniversaries.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-6 text-slate">
            <Cake size={22} strokeWidth={1.5} aria-hidden="true" />
            <p className="text-sm">No anniversaries this month.</p>
          </div>
        )}
        <ul className="flex flex-col gap-3">
          {anniversaries.map(({ employee: e, joinedDay, years }) => (
            <li key={e.id}>
              <Link
                to={`/employees/${e.id}`}
                className="group grid grid-cols-[40px_1fr_auto] items-center gap-3 rounded-btn -mx-1.5 px-1.5 py-1.5 transition-colors hover:bg-slate-50"
              >
                <Avatar employee={e} tone="purple" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy group-hover:text-blue">
                    {e.full_name || `${e.first_name} ${e.last_name}`.trim()}
                  </p>
                  <span className="inline-flex items-center rounded-full bg-purple/10 px-2 py-0.5 text-[11px] font-semibold text-purple">
                    {years} {years === 1 ? "year" : "years"}
                  </span>
                </div>
                <span className="whitespace-nowrap text-[11px] tabular-nums text-slate">
                  {MONTHS[currentMonth].slice(0, 3)} {joinedDay}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pending approvals breakdown card
// ---------------------------------------------------------------------------

function PendingApprovalsCard() {
  const tasks = useQuery({
    queryKey: ["approval-tasks", "pending-list"],
    queryFn: () =>
      api.get<Paginated<ApprovalTask>>(
        "/workflows/tasks/?state=active&ordering=-assigned_at&page_size=20",
      ),
  });

  // Group pending tasks by step_name_snapshot so the panel reads like a
  // "5 leave requests / 2 expense claims / …" digest. Falls back to the
  // raw step name if there's only one bucket.
  const grouped = (() => {
    const map = new Map<string, number>();
    for (const t of tasks.data?.results ?? []) {
      const key = t.step_name_snapshot || "Other";
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  })();

  const total = tasks.data?.count ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare
                size={16}
                strokeWidth={1.75}
                className="text-amber-600"
                aria-hidden="true"
              />
              Pending approvals
            </CardTitle>
            <CardDescription>
              {total > 0
                ? `${total.toLocaleString("en-PH")} pending across all workflows`
                : "Live approval queue"}
            </CardDescription>
          </div>
          <Link
            to="/approvals"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue hover:text-blue-hover"
          >
            View all
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {tasks.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {!tasks.isLoading && grouped.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-6 text-slate">
            <Inbox size={22} strokeWidth={1.5} aria-hidden="true" />
            <p className="text-sm">No pending approvals. You're clear.</p>
          </div>
        )}
        <ul className="flex flex-col gap-2">
          {grouped.map(([name, count]) => (
            <li
              key={name}
              className="grid grid-cols-[36px_1fr_auto] items-center gap-3 rounded-btn border border-slate-200 bg-white px-3 py-2.5"
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-amber-50 text-amber-600"
              >
                <CheckSquare size={16} strokeWidth={1.75} />
              </span>
              <span className="truncate text-sm font-semibold text-navy">
                {name}
              </span>
              <span className="inline-flex h-6 min-w-[28px] items-center justify-center rounded-full bg-amber-100 px-2 text-xs font-bold tabular-nums text-amber-700">
                {count}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Avatar({
  employee,
  tone = "blue",
}: {
  employee: Employee;
  tone?: "blue" | "purple";
}) {
  return (
    <PersonAvatar
      name={employee.full_name || `${employee.first_name} ${employee.last_name}`}
      photo={employee.photo}
      fallback={employee.employee_id}
      tone={tone}
      size={40}
    />
  );
}

function QuickAccessCard() {
  const me = useMyEmployee();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick access</CardTitle>
        <CardDescription>
          {me.data
            ? `Your records (${me.data.employee_id})`
            : "Jump to a module"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {me.isLoading && (
          <p className="text-sm text-slate">Looking up your record…</p>
        )}

        {me.data ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <QuickLink
              to={`/employees/${me.data.id}`}
              icon={UserIcon}
              label="My profile"
            />
            <QuickLink
              to={`/leave-applications?employee=${me.data.id}`}
              icon={CalendarDays}
              label="My leave"
            />
            <QuickLink
              to={`/expenses?employee=${me.data.id}`}
              icon={Receipt}
              label="My expenses"
            />
            <QuickLink
              to={`/salary-slips?employee=${me.data.id}`}
              icon={Wallet}
              label="My payslips"
            />
          </div>
        ) : (
          me.isSuccess && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <QuickLink to="/employees" icon={Users} label="Employees" />
              <QuickLink
                to="/leave-applications"
                icon={CalendarDays}
                label="Leave"
              />
              <QuickLink to="/expenses" icon={Receipt} label="Expenses" />
              <QuickLink to="/salary-slips" icon={Wallet} label="Payslips" />
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof UserIcon;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 rounded-btn border border-slate-200 px-3 py-2.5 text-sm text-foreground transition-colors hover:border-blue/40 hover:bg-blue/5"
    >
      <Icon size={16} strokeWidth={1.5} className="text-blue" aria-hidden="true" />
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function RecentRunsChart() {
  const navigate = useNavigate();
  const runs = useQuery({
    queryKey: ["payroll-entries", "recent-chart"],
    queryFn: () =>
      api.get<Paginated<PayrollEntry>>(
        "/payroll/payroll-entries/?ordering=-created_at&page_size=12&state=submitted",
      ),
  });

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Banknote
                size={16}
                strokeWidth={1.75}
                className="text-green"
                aria-hidden="true"
              />
              Recent payroll runs
            </CardTitle>
            <CardDescription>
              Gross vs net for the last 12 approved runs
            </CardDescription>
          </div>
          <Link
            to="/payroll-runs"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue hover:text-blue-hover"
          >
            All runs
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {runs.isLoading && <Skeleton className="h-40 w-full" />}
        {runs.isError && (
          <ErrorMessage error={runs.error} fallback="Couldn't load runs." />
        )}
        {runs.isSuccess && runs.data.results.length === 0 && (
          <p className="py-8 text-center text-sm text-slate">
            No approved runs yet — run and approve one from the{" "}
            <Link to="/payroll-runs" className="text-blue underline-offset-4 hover:underline">
              Payroll runs
            </Link>{" "}
            page.
          </p>
        )}
        {runs.isSuccess && runs.data.results.length > 0 && (
          <BarChart
            primaryLabel="Gross"
            secondaryLabel="Net"
            formatValue={(v) => formatPHPCompact(v)}
            data={[...runs.data.results].reverse().map((row) => ({
              label:
                (row.name && row.name.length > 14
                  ? row.name.slice(0, 12) + "…"
                  : row.name) ||
                row.id.slice(0, 8),
              value: Number.parseFloat(row.total_gross || "0"),
              secondaryValue: Number.parseFloat(row.total_net || "0"),
              onClick: () =>
                navigate(`/salary-slips?payroll_entry=${row.id}`),
            }))}
          />
        )}
      </CardContent>
    </Card>
  );
}
